/**
 * CustomerOutstandingChart Component - Phase 5 Implementation
 * Bar chart for customer aging analysis
 * Shows outstanding amounts by age buckets and customer status
 */

import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  Legend,
  ReferenceLine
} from 'recharts';
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  type ChartConfig
} from '../../ui/chart';
import { Skeleton } from '../../ui/skeleton';
import { Users, Clock, AlertTriangle, DollarSign } from 'lucide-react';
import { cn } from '../../../lib/utils';

interface CustomerOutstandingChartProps {
  data: any[];
  isLoading?: boolean;
  error?: string;
  className?: string;
}

const chartConfig = {
  current: {
    label: "Current (0-30 days)",
    color: "hsl(var(--chart-3))",
  },
  days30: {
    label: "31-60 days",
    color: "hsl(var(--chart-2))",
  },
  days60: {
    label: "61-90 days", 
    color: "hsl(var(--chart-5))",
  },
  days90: {
    label: "90+ days",
    color: "hsl(var(--chart-4))",
  },
} satisfies ChartConfig;

// Mock data for demonstration
const generateMockData = () => [
  { 
    customer: 'ABC Corp', 
    current: 45000, 
    days30: 12000, 
    days60: 0, 
    days90: 0,
    total: 57000,
    daysOutstanding: 25,
    status: 'good'
  },
  { 
    customer: 'XYZ Ltd', 
    current: 23000, 
    days30: 18000, 
    days60: 8000, 
    days90: 0,
    total: 49000,
    daysOutstanding: 45,
    status: 'warning'
  },
  { 
    customer: 'Tech Solutions', 
    current: 0, 
    days30: 15000, 
    days60: 25000, 
    days90: 12000,
    total: 52000,
    daysOutstanding: 85,
    status: 'overdue'
  },
  { 
    customer: 'Global Enterprises', 
    current: 67000, 
    days30: 0, 
    days60: 0, 
    days90: 0,
    total: 67000,
    daysOutstanding: 15,
    status: 'good'
  },
  { 
    customer: 'Retail Partners', 
    current: 0, 
    days30: 0, 
    days60: 15000, 
    days90: 28000,
    total: 43000,
    daysOutstanding: 95,
    status: 'critical'
  },
  { 
    customer: 'Manufacturing Inc', 
    current: 34000, 
    days30: 12000, 
    days60: 0, 
    days90: 0,
    total: 46000,
    daysOutstanding: 35,
    status: 'good'
  },
  { 
    customer: 'Service Co', 
    current: 0, 
    days30: 22000, 
    days60: 18000, 
    days90: 15000,
    total: 55000,
    daysOutstanding: 75,
    status: 'overdue'
  },
];

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'good':
      return 'text-green-600';
    case 'warning':
      return 'text-yellow-600';
    case 'overdue':
      return 'text-orange-600';
    case 'critical':
      return 'text-red-600';
    default:
      return 'text-muted-foreground';
  }
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0]?.payload;
    return (
      <div className="bg-background border rounded-lg shadow-lg p-3 min-w-[240px]">
        <p className="font-medium mb-2">{label}</p>
        <div className="space-y-1">
          {payload.map((entry: any, index: number) => {
            if (entry.value === 0) return null;
            return (
              <div key={index} className="flex items-center justify-between text-sm">
                <div className="flex items-center space-x-2">
                  <div 
                    className="w-3 h-3 rounded"
                    style={{ backgroundColor: entry.color }}
                  />
                  <span className="text-muted-foreground">{entry.name}:</span>
                </div>
                <span className="font-medium">{formatCurrency(entry.value)}</span>
              </div>
            );
          })}
          <div className="pt-2 border-t mt-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Total Outstanding:</span>
              <span className="font-medium">{formatCurrency(data?.total || 0)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Days Outstanding:</span>
              <span className={cn("font-medium", getStatusColor(data?.status))}>
                {data?.daysOutstanding} days
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Status:</span>
              <span className={cn("font-medium capitalize", getStatusColor(data?.status))}>
                {data?.status}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

export const CustomerOutstandingChart: React.FC<CustomerOutstandingChartProps> = ({
  data,
  isLoading = false,
  error,
  className
}) => {
  // Use mock data if no data provided or in loading state
  const chartData = data.length > 0 ? data : generateMockData();

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center space-y-2">
          <p className="text-destructive font-medium">Failed to load chart</p>
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <Skeleton className="h-6 w-32" />
          <div className="flex space-x-4">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-20" />
          </div>
        </div>
        <Skeleton className="h-64 w-full" />
        <div className="flex space-x-6 justify-center">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-16" />
        </div>
      </div>
    );
  }

  // Calculate summary metrics
  const totalOutstanding = chartData.reduce((sum, item) => sum + item.total, 0);
  const overdueAmount = chartData.reduce((sum, item) => sum + (item.days60 + item.days90), 0);
  const averageDays = chartData.reduce((sum, item) => sum + item.daysOutstanding, 0) / chartData.length;
  const criticalCustomers = chartData.filter(item => item.status === 'critical' || item.status === 'overdue').length;

  return (
    <div className={cn("space-y-4", className)}>
      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 dark:bg-blue-950/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-700 dark:text-blue-300">Total Outstanding</p>
              <p className="text-xl font-bold text-blue-800 dark:text-blue-200">
                {formatCurrency(totalOutstanding)}
              </p>
            </div>
            <div className="text-blue-600">
              <DollarSign className="h-6 w-6" />
            </div>
          </div>
        </div>
        
        <div className="bg-red-50 dark:bg-red-950/20 rounded-lg p-4 border border-red-200 dark:border-red-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-700 dark:text-red-300">Overdue (60+ days)</p>
              <p className="text-xl font-bold text-red-800 dark:text-red-200">
                {formatCurrency(overdueAmount)}
              </p>
            </div>
            <div className="text-red-600">
              <AlertTriangle className="h-6 w-6" />
            </div>
          </div>
        </div>
        
        <div className="bg-yellow-50 dark:bg-yellow-950/20 rounded-lg p-4 border border-yellow-200 dark:border-yellow-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-yellow-700 dark:text-yellow-300">Avg Days Outstanding</p>
              <p className="text-xl font-bold text-yellow-800 dark:text-yellow-200">
                {averageDays.toFixed(0)} days
              </p>
            </div>
            <div className="text-yellow-600">
              <Clock className="h-6 w-6" />
            </div>
          </div>
        </div>
        
        <div className={cn(
          "rounded-lg p-4 border",
          criticalCustomers > 0 
            ? "bg-orange-50 dark:bg-orange-950/20 border-orange-200 dark:border-orange-800"
            : "bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800"
        )}>
          <div className="flex items-center justify-between">
            <div>
              <p className={cn(
                "text-sm",
                criticalCustomers > 0 
                  ? "text-orange-700 dark:text-orange-300"
                  : "text-green-700 dark:text-green-300"
              )}>
                At Risk Customers
              </p>
              <p className={cn(
                "text-xl font-bold",
                criticalCustomers > 0 
                  ? "text-orange-800 dark:text-orange-200"
                  : "text-green-800 dark:text-green-200"
              )}>
                {criticalCustomers}
              </p>
            </div>
            <div className={cn(
              criticalCustomers > 0 ? "text-orange-600" : "text-green-600"
            )}>
              <Users className="h-6 w-6" />
            </div>
          </div>
        </div>
      </div>

      {/* Aging Analysis Chart */}
      <ChartContainer config={chartConfig} className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 60,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis 
              dataKey="customer" 
              stroke="hsl(var(--muted-foreground))"
              fontSize={12}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis 
              tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
              stroke="hsl(var(--muted-foreground))"
              fontSize={12}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend content={<ChartLegendContent />} />
            
            <Bar
              dataKey="current"
              stackId="a"
              fill="var(--color-current)"
              name="Current (0-30 days)"
              radius={[0, 0, 0, 0]}
            />
            <Bar
              dataKey="days30"
              stackId="a"
              fill="var(--color-days30)"
              name="31-60 days"
              radius={[0, 0, 0, 0]}
            />
            <Bar
              dataKey="days60"
              stackId="a"
              fill="var(--color-days60)"
              name="61-90 days"
              radius={[0, 0, 0, 0]}
            />
            <Bar
              dataKey="days90"
              stackId="a"
              fill="var(--color-days90)"
              name="90+ days"
              radius={[2, 2, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </ChartContainer>
    </div>
  );
};